/*
 * Author: zoesvision.com
 * Purpose: Shared one-time viewport reveal behavior for opt-in z-components.
 */

if (!customElements.get('z-animate-on-scroll')) {
  customElements.define(
    'z-animate-on-scroll',
    class ZAnimateOnScroll extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.initialized = true;
        this.motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
        this.handleEntries = this.handleEntries.bind(this);
        this.handleMotionChange = this.handleMotionChange.bind(this);
        this.motionQuery.addEventListener('change', this.handleMotionChange);
        this.observer = new IntersectionObserver(this.handleEntries, { threshold: 0.15 });
        this.refresh();
        this.dataset.zScrollReady = 'true';
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.motionQuery?.removeEventListener('change', this.handleMotionChange);
        this.initialized = false;
      }

      handleEntries(entries) {
        // zoesvision.com | 2026-08-16 | Purpose: Stagger only cards revealed in the same carousel viewport batch.
        const visibleEntries = entries.filter((entry) => entry.isIntersecting);
        // zoesvision.com | 2026-08-17 | Purpose: Follow the current visual order after Embla repositions looped slides.
        if (this.dataset.zScrollDynamicStagger === 'true') {
          visibleEntries.sort((first, second) => {
            return first.boundingClientRect.left - second.boundingClientRect.left;
          });
        }
        visibleEntries.forEach((entry, visibleIndex) => {
          if (this.dataset.zScrollDynamicStagger === 'true') {
            entry.target.style.setProperty(
              '--z-scroll-delay',
              `${this.initialDelay + visibleIndex * this.delayStep}ms`
            );
          }
          this.reveal(entry.target);
          this.observer.unobserve(entry.target);
        });
      }

      configureItems() {
        const animation = this.dataset.zScrollAnimation || 'none';
        const duration = Number(this.dataset.zScrollDuration) || 0;
        const initialDelay = Number(this.dataset.zScrollDelay) || 0;
        const delayStep = Number(this.dataset.zScrollDelayStep) || 0;
        this.initialDelay = initialDelay;
        this.delayStep = delayStep;
        const header = this.querySelector('[data-z-grid-header]');
        const headerOffset = header && !header.hidden ? 1 : 0;
        const sequenceIndexes = new Map();
        let defaultIndex = headerOffset;

        this.items.forEach((item) => {
          let index = 0;
          const sequence = item.closest('[data-z-scroll-sequence]');

          if (item === header) {
            index = 0;
          } else if (sequence) {
            const key = sequence.dataset.zScrollSequence;
            index = headerOffset + (sequenceIndexes.get(key) || 0);
            sequenceIndexes.set(key, (sequenceIndexes.get(key) || 0) + 1);
          } else {
            index = defaultIndex;
            defaultIndex += 1;
          }

          // zoesvision.com | 2026-07-28 | Purpose: Let a grid header's nested z-heading own its reveal animation.
          item.dataset.zScrollAnimation = item === header ? 'none' : animation;
          item.style.setProperty('--z-scroll-duration', `${duration}ms`);
          const itemDelay = this.dataset.zScrollDynamicStagger === 'true'
            ? initialDelay
            : initialDelay + index * delayStep;
          item.style.setProperty('--z-scroll-delay', `${itemDelay}ms`);
        });
      }

      refresh() {
        this.observer.disconnect();
        // zoesvision.com | 2026-08-16 | Purpose: Let a section identify generated direct block wrappers without marking nested reusable blocks.
        const itemSelector = this.dataset.zScrollItemSelector || '[data-z-scroll-item]';
        this.items = Array.from(this.querySelectorAll(itemSelector));
        this.items.forEach((item) => item.setAttribute('data-z-scroll-item', ''));
        this.configureItems();

        if (this.motionQuery.matches) {
          this.revealAll();
        } else {
          this.items
            .filter((item) => item.dataset.zScrollRevealed !== 'true')
            .forEach((item) => this.observer.observe(item));
        }
      }

      handleMotionChange(event) {
        if (!event.matches) return;

        this.revealAll();
        this.observer.disconnect();
      }

      reveal(item) {
        const selfDuration = parseFloat(
          getComputedStyle(item).getPropertyValue('--z-scroll-duration')
        );
        if (item.dataset.zScrollAnimation !== 'none' && selfDuration > 0) {
          item.dataset.zScrollSelfPlaying = 'true';
        }

        if (this.dataset.zScrollNestedReveals !== 'false') {
          item.querySelectorAll('.z-reveal:not([data-z-animation="none"])').forEach((reveal) => {
            const duration = parseFloat(getComputedStyle(reveal).getPropertyValue('--z-reveal-duration'));
            if (duration > 0) reveal.dataset.zScrollPlaying = 'true';
          });
        }
        item.dataset.zScrollRevealed = 'true';
      }

      revealAll() {
        this.items.forEach((item) => {
          delete item.dataset.zScrollSelfPlaying;
          item.querySelectorAll('[data-z-scroll-playing]').forEach((reveal) => {
            delete reveal.dataset.zScrollPlaying;
          });
          item.dataset.zScrollRevealed = 'true';
        });
      }
    }
  );
}
